import React, { createContext, useContext, useEffect, useState } from 'react';
import { AxiosError } from 'axios';
import ListingsService from '../../../services/Listings.service';
import OffersService from '../../../services/Offers.service';
import toastify from '../../lib/toastify';
import { getIdFromURL } from '../../utils/getIdFromURL';
import {
  PageableInterface,
  ProposalsContextProviderProps,
  ProposalsContextReturn,
  ProposalsFetch,
  ProposalsInterface
} from './types/proposals';
import { scrollWindowTo } from '../../utils/scroll';
import { AdCompatibilityInterface } from './types/ad-compatibility-interface';
import { useLocation } from 'react-router-dom';

export const ProposalsContext = createContext<ProposalsContextReturn | undefined>(undefined);

export function ProposalsContextProvider({ children }: ProposalsContextProviderProps): JSX.Element {
  const location = useLocation();
  const [proposals, setProposals] = useState<ProposalsInterface[]>([]);
  const [currentAd, setCurrentAd] = useState<AdCompatibilityInterface>();
  const [currentProposal, setCurrentProposal] = useState<ProposalsInterface>();
  const [pageable, setPageable] = useState<PageableInterface | undefined>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<unknown | null | string>(null);
  const [fetchNextPage, setFetchNextPage] = useState(1);
  const [fetchingNextPage, setFetchingNextPage] = useState(false);
  const [transition, setTransition] = useState(false);

  useEffect(() => {
    const idFromURL = getIdFromURL(location.pathname);
    if (error) return;
    if (!idFromURL) return;
    if (loading) return;
    if (fetchNextPage - 1 === pageable?.pageNumber) return;

    async function getProposals(page = 1) {
      if (fetchNextPage > 1) {
        setFetchingNextPage(true);
      } else {
        scrollWindowTo(0);
        setLoading(true);
      }

      try {
        if (!currentAd) {
          const response = await ListingsService.getById(idFromURL);
          setCurrentAd(response);
        }

        const { content, pageable, last } = (await OffersService.get({
          id: idFromURL,
          page
        })) as ProposalsFetch;

        if (content) {
          setProposals(prevProps => [...prevProps, ...content]);
        }

        if (pageable) {
          setPageable({
            pageNumber: pageable.pageNumber,
            pageSize: pageable.pageSize,
            nextPage: pageable.pageNumber + 2,
            isLastPage: last
          });
        }
      } catch (err) {
        const axiosError = err as AxiosError;
        if (axiosError?.response?.status !== 404) {
          toastify.error(
            'falha na requisição',
            'não foi possível buscar suas mensagens neste momento'
          );
        }
        setError(err);
      } finally {
        setLoading(false);
        setFetchingNextPage(false);
      }
    }

    getProposals(fetchNextPage);
  }, [fetchNextPage, pageable?.pageNumber, currentAd, loading, error, location.pathname]);

  useEffect(() => {
    scrollWindowTo(0);
  }, [currentProposal]);

  return (
    <ProposalsContext.Provider
      value={{
        currentAd,
        proposals,
        setFetchNextPage,
        pageable,
        loading,
        error,
        fetchingNextPage,
        currentProposal,
        setCurrentProposal,
        transition,
        setTransition
      }}
    >
      {children}
    </ProposalsContext.Provider>
  );
}

export function useProposalsContext(): ProposalsContextReturn {
  const context = useContext(ProposalsContext);

  if (!context) {
    throw new Error('Proposals is not available');
  }

  return context;
}
